import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { DollarSign, TrendingUp, Users, Wallet, Plus, Upload, FileText, Dumbbell, X, Ticket, Trash2, Percent } from "lucide-react";
import { toast } from "sonner";

const IMG_OPTIONS = [
  "https://images.unsplash.com/photo-1605296867304-46d5465a13f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxmaXRuZXNzJTIwdHJhaW5lciUyMGd5bSUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc5MDEwNzk2NXww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1543352632-5a4b24e4d2a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxoZWFsdGh5JTIwbWVhbCUyMHByZXAlMjBib3dsfGVufDB8fHx8MTc5MDEwNzk2NHww&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/10895295/pexels-photo-10895295.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-5 hover:-translate-y-1 hover:border-[#FF6B6B]/40 transition-all duration-200">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-[0.15em] text-[#A0A0A0]">{label}</span>
        <Icon className="w-5 h-5" style={{ color: accent }} />
      </div>
      <p className="font-display font-black text-3xl text-white">{value}</p>
    </div>
  );
}

export default function InstructorStudio() {
  const { user } = useAuth();
  const [tab, setTab] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [plans, setPlans] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState({ code: "", percent_off: 15, expires_at: "", max_redemptions: "" });
  const [showCreate, setShowCreate] = useState(false);
  const [ctype, setCtype] = useState("post");
  const [form, setForm] = useState({ title: "", description: "", price: 29, type: "workout", cover: IMG_OPTIONS[0], caption: "", media_url: "" });

  const load = useCallback(async () => {
    const [a, o, c, p, cp] = await Promise.all([
      api.get("/instructor/analytics"), api.get("/orders"),
      api.get("/instructor/clients"), api.get("/plans"),
      api.get(`/payments/coupons?instructor_id=${user.user_id}`),
    ]);
    setAnalytics(a.data); setOrders(o.data); setClients(c.data);
    setPlans(p.data.filter((x) => x.instructor_id === user.user_id));
    setCoupons(cp.data);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const createCoupon = async () => {
    const code = couponForm.code.trim().toUpperCase();
    if (!code) return toast.error("Enter a code");
    try {
      await api.post("/payments/coupons", {
        code, percent_off: Number(couponForm.percent_off),
        expires_at: couponForm.expires_at ? new Date(couponForm.expires_at + "T23:59:59Z").toISOString() : null,
        max_redemptions: couponForm.max_redemptions ? Number(couponForm.max_redemptions) : null,
      });
      toast.success(`Code ${code} created — ${couponForm.percent_off}% off`);
      setCouponForm({ code: "", percent_off: 15, expires_at: "", max_redemptions: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create code"); }
  };

  const deleteCoupon = async (id) => {
    await api.delete(`/payments/coupons/${id}`);
    toast.success("Code deactivated");
    load();
  };

  const submitCreate = async () => {
    try {
      if (ctype === "plan") {
        await api.post("/plans", { title: form.title, description: form.description, price: Number(form.price), type: form.type, cover: form.cover });
        toast.success("Plan published!");
      } else {
        await api.post("/posts", { media_url: form.media_url || "https://videos.pexels.com/video-files/4761426/4761426-uhd_1440_2560_25fps.mp4", thumbnail: form.cover, caption: form.caption, type: "reel" });
        toast.success("Reel published to feed!");
      }
      setShowCreate(false);
      load();
    } catch { toast.error("Failed to publish"); }
  };

  const trendData = analytics?.trend || [];

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#4ECCD3]">Monetization Studio</p>
          <h1 className="font-display font-bold text-4xl uppercase tracking-tight text-white">Coach {user?.name?.split(" ")[0]}</h1>
        </div>
        <button data-testid="create-content-btn" onClick={() => setShowCreate(true)} className="px-5 py-2.5 rounded-full bg-[#FF6B6B] text-black font-bold uppercase tracking-wide text-sm flex items-center gap-2 hover:brightness-110 transition-all">
          <Plus className="w-4 h-4" /> Create Content
        </button>
      </div>

      <div className="flex gap-2 mb-6 border-b border-[#2A2A2A]">
        {["analytics", "content", "clients", "coupons", "payouts"].map((t) => (
          <button key={t} data-testid={`studio-tab-${t}`} onClick={() => setTab(t)} className={`px-4 py-3 text-sm font-semibold capitalize transition-colors border-b-2 -mb-px ${tab === t ? "text-[#FF6B6B] border-[#FF6B6B]" : "text-[#A0A0A0] border-transparent hover:text-white"}`}>{t}</button>
        ))}
      </div>

      {tab === "analytics" && analytics && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={TrendingUp} label="MRR" value={`$${analytics.mrr}`} accent="#FF6B6B" />
            <Stat icon={DollarSign} label="Total Revenue" value={`$${analytics.total_revenue}`} accent="#4ECCD3" />
            <Stat icon={Users} label="Active Clients" value={analytics.active_clients} accent="#FF6B6B" />
            <Stat icon={Wallet} label="Pending Payout" value={`$${analytics.payout_pending}`} accent="#4ECCD3" />
          </div>
          <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-6">
            <h3 className="font-bold text-white mb-1">Monthly Revenue</h3>
            <p className="text-xs text-[#A0A0A0] mb-4">Last 6 months</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={trendData}>
                <XAxis dataKey="month" stroke="#A0A0A0" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis hide />
                <Tooltip cursor={{ fill: "#2A2A2A" }} contentStyle={{ background: "#121212", border: "1px solid #2A2A2A", borderRadius: 8, color: "#fff" }} />
                <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                  {trendData.map((_, i) => <Cell key={i} fill={i === trendData.length - 1 ? "#FF6B6B" : "#4ECCD3"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Stat icon={FileText} label="Product Sales" value={`$${analytics.product_sales}`} accent="#FF6B6B" />
            <Stat icon={Dumbbell} label="1-on-1 Earnings" value={`$${analytics.session_earnings}`} accent="#4ECCD3" />
            <Stat icon={DollarSign} label="Tips" value={`$${analytics.tips}`} accent="#FF6B6B" />
          </div>
          <div className="bg-[#4ECCD3]/5 border border-[#4ECCD3]/30 rounded-2xl p-5">
            <p className="text-sm text-[#4ECCD3] font-semibold">💳 Flexible financing available on high-ticket coaching packages ($500+). Offer clients monthly installments at checkout.</p>
          </div>
        </div>
      )}

      {tab === "content" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {plans.length === 0 && <p className="text-sm text-[#A0A0A0] col-span-full">No published content yet. Hit "Create Content" to publish your first program.</p>}
          {plans.map((p) => (
            <div key={p.plan_id} className="rounded-xl overflow-hidden border border-[#2A2A2A] group" data-testid={`content-card-${p.plan_id}`}>
              <div className="h-28 overflow-hidden"><img src={p.cover} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /></div>
              <div className="p-3">
                <span className="text-[10px] uppercase tracking-wider text-[#FF6B6B]">{p.type}</span>
                <p className="text-sm font-semibold text-white line-clamp-1">{p.title}</p>
                <p className="text-xs text-[#A0A0A0] mt-1">${p.price}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "clients" && (
        <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl overflow-hidden">
          {clients.length === 0 ? <p className="text-sm text-[#A0A0A0] p-6">No clients yet.</p> : clients.map((c) => (
            <div key={c.user_id} className="flex items-center gap-4 p-4 border-b border-[#2A2A2A] last:border-0" data-testid={`client-row-${c.user_id}`}>
              <img src={c.picture} alt="" className="w-11 h-11 rounded-full object-cover" />
              <div className="flex-1">
                <p className="font-semibold text-white text-sm">{c.name}</p>
                <p className="text-xs text-[#A0A0A0]">Spent ${c.total_spent}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-[#A0A0A0] mb-1">Adherence</p>
                <div className="w-24 h-2 rounded-full bg-[#121212] overflow-hidden">
                  <div className="h-full bg-[#FF6B6B]" style={{ width: `${c.adherence}%` }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "coupons" && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-5 h-fit">
            <div className="flex items-center gap-2 mb-4"><Ticket className="w-5 h-5 text-[#FF6B6B]" /><h3 className="font-bold text-white">Create Discount Code</h3></div>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#A0A0A0]">Code</label>
                <input data-testid="coupon-code-input" value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })} placeholder="e.g. SUMMER20" className="w-full mt-1 bg-[#121212] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-sm text-white uppercase placeholder-[#A0A0A0] focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#A0A0A0]">Percent off: {couponForm.percent_off}%</label>
                <input data-testid="coupon-percent-range" type="range" min="5" max="100" step="5" value={couponForm.percent_off} onChange={(e) => setCouponForm({ ...couponForm, percent_off: e.target.value })} className="w-full mt-2 accent-[#FF6B6B]" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A0A0A0]">Expires (optional)</label>
                  <input data-testid="coupon-expiry-input" type="date" value={couponForm.expires_at} onChange={(e) => setCouponForm({ ...couponForm, expires_at: e.target.value })} className="w-full mt-1 bg-[#121212] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A0A0A0]">Max uses (optional)</label>
                  <input data-testid="coupon-maxredemptions-input" type="number" min="1" value={couponForm.max_redemptions} onChange={(e) => setCouponForm({ ...couponForm, max_redemptions: e.target.value })} placeholder="∞" className="w-full mt-1 bg-[#121212] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#A0A0A0] focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]" />
                </div>
              </div>
              <button data-testid="create-coupon-btn" onClick={createCoupon} className="w-full py-3 rounded-xl bg-[#FF6B6B] text-black font-bold uppercase tracking-wide text-sm flex items-center justify-center gap-2 hover:brightness-110 transition-all">
                <Percent className="w-4 h-4" /> Create Code
              </button>
              <p className="text-xs text-[#A0A0A0]">Members enter your code on the Stripe checkout page to get the discount.</p>
            </div>
          </div>
          <div className="lg:col-span-2">
            {coupons.length === 0 ? (
              <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-10 text-center text-[#A0A0A0]">No discount codes yet. Create one to boost conversions.</div>
            ) : (
              <div className="space-y-3">
                {coupons.map((c) => (
                  <div key={c.coupon_id} data-testid={`coupon-row-${c.coupon_id}`} className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl bg-[#FF6B6B]/10 flex items-center justify-center"><Ticket className="w-5 h-5 text-[#FF6B6B]" /></div>
                      <div>
                        <p className="font-display font-bold text-lg tracking-tight text-white">{c.code}</p>
                        <p className="text-xs text-[#A0A0A0]">
                          {c.percent_off}% off
                          {c.expires_at ? ` · expires ${new Date(c.expires_at).toLocaleDateString()}` : ""}
                          {c.max_redemptions ? ` · max ${c.max_redemptions} uses` : ""}
                        </p>
                      </div>
                    </div>
                    <button data-testid={`delete-coupon-${c.coupon_id}`} onClick={() => deleteCoupon(c.coupon_id)} className="w-9 h-9 rounded-lg bg-[#2A2A2A] text-[#A0A0A0] hover:text-[#FF3B30] flex items-center justify-center transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "payouts" && (
        <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl overflow-hidden">
          <div className="grid grid-cols-4 px-4 py-3 text-xs uppercase tracking-wider text-[#A0A0A0] border-b border-[#2A2A2A]">
            <span>Item</span><span>Type</span><span>Amount</span><span className="text-right">Status</span>
          </div>
          {orders.length === 0 ? <p className="text-sm text-[#A0A0A0] p-6">No sales yet.</p> : orders.map((o) => (
            <div key={o.order_id} className="grid grid-cols-4 px-4 py-3 text-sm border-b border-[#2A2A2A] last:border-0 items-center" data-testid={`payout-row-${o.order_id}`}>
              <span className="text-white truncate">{o.item_title}</span>
              <span className="text-[#A0A0A0] capitalize">{o.item_type}</span>
              <span className="text-white">${o.amount}</span>
              <span className={`text-right text-xs font-semibold ${o.payout_status === "paid" ? "text-[#FF6B6B]" : "text-[#4ECCD3]"}`}>{o.payout_status}</span>
            </div>
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="create-content-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-2xl uppercase tracking-tight text-white">Publish</h3>
              <button data-testid="close-create-modal" onClick={() => setShowCreate(false)} className="text-[#A0A0A0] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex gap-2 mb-4">
              {[{ id: "post", label: "Reel", icon: Upload }, { id: "plan", label: "Plan/Guide", icon: FileText }].map((t) => (
                <button key={t.id} data-testid={`ctype-${t.id}`} onClick={() => setCtype(t.id)} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border flex items-center justify-center gap-2 transition-colors ${ctype === t.id ? "border-[#FF6B6B] bg-[#FF6B6B]/10 text-[#FF6B6B]" : "border-[#2A2A2A] text-[#A0A0A0]"}`}>
                  <t.icon className="w-4 h-4" /> {t.label}
                </button>
              ))}
            </div>
            <div className="space-y-3">
              {ctype === "plan" ? (
                <>
                  <input data-testid="plan-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Plan title" className="w-full bg-[#121212] border border-[#2A2A2A] rounded-xl px-4 py-3 text-sm text-white placeholder-[#A0A0A0] focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]" />
                  <textarea data-testid="plan-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={2} className="w-full bg-[#121212] border border-[#2A2A2A] rounded-xl px-4 py-3 text-sm text-white placeholder-[#A0A0A0] focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]" />
                  <div className="flex gap-2">
                    <select data-testid="plan-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="flex-1 bg-[#121212] border border-[#2A2A2A] rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]">
                      <option value="workout">Workout</option><option value="meal">Meal Plan</option><option value="mental">Mental Health</option>
                    </select>
                    <input data-testid="plan-price-input" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Price $" className="w-28 bg-[#121212] border border-[#2A2A2A] rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]" />
                  </div>
                </>
              ) : (
                <textarea data-testid="post-caption-input" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} placeholder="Caption your reel…" rows={3} className="w-full bg-[#121212] border border-[#2A2A2A] rounded-xl px-4 py-3 text-sm text-white placeholder-[#A0A0A0] focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]" />
              )}
              <div>
                <p className="text-xs text-[#A0A0A0] mb-2 uppercase tracking-wider">Cover image</p>
                <div className="flex gap-2">
                  {IMG_OPTIONS.map((img) => (
                    <button key={img} data-testid="cover-option" onClick={() => setForm({ ...form, cover: img })} className={`w-20 h-14 rounded-lg overflow-hidden border-2 ${form.cover === img ? "border-[#FF6B6B]" : "border-transparent"}`}>
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
              <button data-testid="publish-content-btn" onClick={submitCreate} className="w-full py-3 rounded-xl bg-[#FF6B6B] text-black font-bold uppercase tracking-wide text-sm hover:brightness-110 transition-all">Publish</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
